import { z } from 'zod'
import type { FullBackup, TrainingProgram } from '../types'
import { getDb, saveSettings } from '../storage/db'

const backupEnvelopeSchema = z.object({
  schemaVersion: z.literal(1),
  app: z.literal('personal-trainer'),
  exportedAt: z.string(),
  data: z.object({
    profile: z.array(z.record(z.string(), z.unknown())),
    settings: z.array(z.record(z.string(), z.unknown())),
    programs: z.array(z.record(z.string(), z.unknown())),
    exercises: z.array(z.record(z.string(), z.unknown())),
    workouts: z.array(z.record(z.string(), z.unknown())),
    workoutExercises: z.array(z.record(z.string(), z.unknown())),
    measurements: z.array(z.record(z.string(), z.unknown())),
    backupsMetadata: z.array(z.record(z.string(), z.unknown()))
  })
})

const stores = ['profile', 'settings', 'programs', 'exercises', 'workouts', 'workoutExercises', 'measurements', 'backupsMetadata'] as const

export async function createFullBackup(): Promise<FullBackup> {
  const db = await getDb()
  const values = await Promise.all(stores.map((store) => db.getAll(store)))
  return {
    schemaVersion: 1,
    app: 'personal-trainer',
    exportedAt: new Date().toISOString(),
    data: Object.fromEntries(stores.map((store, index) => [store, values[index]])) as FullBackup['data']
  }
}

export function parseBackup(text: string): FullBackup {
  let json: unknown
  try { json = JSON.parse(text) } catch { throw new Error('Файл не является корректным JSON') }
  const parsed = backupEnvelopeSchema.safeParse(json)
  if (!parsed.success) {
    const version = typeof json === 'object' && json && 'schemaVersion' in json ? (json as { schemaVersion?: unknown }).schemaVersion : undefined
    if (version !== 1) throw new Error('Эта версия backup пока не поддерживается')
    throw new Error('Структура backup повреждена или неполна')
  }
  return json as FullBackup
}

export function backupSummary(backup: FullBackup) {
  return {
    exportedAt: backup.exportedAt,
    workouts: backup.data.workouts.length,
    programs: backup.data.programs.length,
    measurements: backup.data.measurements.length
  }
}

export async function restoreBackup(backup: FullBackup) {
  const db = await getDb()
  const tx = db.transaction([...stores], 'readwrite')
  for (const store of stores) {
    const target = tx.objectStore(store)
    await target.clear()
    for (const value of backup.data[store] as unknown[]) await target.put(value as never)
  }
  await tx.done
}

export async function markBackupCreated(filename: string) {
  const db = await getDb()
  const now = new Date().toISOString()
  await db.put('backupsMetadata', { id: crypto.randomUUID(), createdAt: now, filename })
  const settings = await db.get('settings', 'settings')
  if (settings) await saveSettings({ ...settings, lastBackupAt: now })
}

export function programImportSummary(program: TrainingProgram, current?: TrainingProgram) {
  const currentMap = new Map(current?.exerciseLibrary.map((e) => [e.id, JSON.stringify(e)]) || [])
  return {
    added: program.exerciseLibrary.filter((e) => !currentMap.has(e.id)).length,
    changed: program.exerciseLibrary.filter((e) => currentMap.has(e.id) && currentMap.get(e.id) !== JSON.stringify(e)).length
  }
}
